import { ChevronLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'

interface DetailHeaderProps {
  /** Where the back-link points (list page). Opcjonalne - jeśli pusto, nie renderujemy
   *  back-linka (Topbar pokazuje już sekcję, więc duplikacja nie jest potrzebna). */
  backTo?: string
  /** Label of the back-link, e.g. "Umowy", "Klienci". */
  backLabel?: string
  /** Main heading. */
  title: ReactNode
  /** Secondary line under the title (mono code, hint, etc). */
  subtitle?: ReactNode
  /** Optional badges/chips rendered next to the subtitle. */
  badges?: ReactNode
  /** Right-side actions (buttons). */
  actions?: ReactNode
}

/**
 * Unified header for list→detail pages. Provides a consistent title area and
 * right-side action slot. Back-link jest opcjonalny - dla stron, na których
 * Topbar już sygnalizuje bieżącą sekcję, można go pominąć.
 */
export function DetailHeader({ backTo, backLabel, title, subtitle, badges, actions }: DetailHeaderProps) {
  const showBackLink = backTo && backLabel
  return (
    <header className="px-6 py-4 bg-white border-b border-slate-200">
      {showBackLink && (
        <Link
          to={backTo!}
          className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-sky-700 transition-colors"
        >
          <ChevronLeft className="size-3.5" />
          {backLabel}
        </Link>
      )}
      <div className={(showBackLink ? 'mt-1.5 ' : '') + 'flex items-start justify-between gap-4'}>
        <div className="flex flex-col gap-1 min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900 truncate">{title}</h1>
          {(subtitle || badges) && (
            <div className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
              {subtitle}
              {badges}
            </div>
          )}
        </div>
        {actions && <div className="shrink-0 flex items-center gap-2">{actions}</div>}
      </div>
    </header>
  )
}
